import { useEffect, useState, FC } from "react";
import { Topic } from "roslib";

import { COMPASS_TOPIC } from "../../constants/topics";
import { useRos } from "../../services/Ros/utils/rosProvider";
import { useMsg } from "../../services/Ros/Subscriber";
import { subscribe, buildTopicOptions, unsubscribe } from "../../services/Ros/utils/rosTopicCommands";
import useDeepCompareEffect from "../../services/General/useDeepCompareEffect";
import { useRobotState } from "../../services/Ros/RobotState";

interface IGPSMessage {
  altitude?: number;
  header?: {
    frame_id: string;
    seq: number;
    stamp: {
      secs: number;
      nsecs: number;
    };
  };
  latitude: number;
  longitude: number;
  position_covariance?: Array<number>;
  position_covariance_type?: number;
  status?: {
    status: number;
    service: number;
  };
}

interface RobotTrackingMarkerProps extends google.maps.MarkerOptions {
  centerOnRobot?: boolean;
}

const canHavePointer = ["READY","AUTO","MANUAL"]

export const RobotTrackingMarker: FC<RobotTrackingMarkerProps> = (options) => {
  const { centerOnRobot, ...markerOptions } = options;
  const ROS = useRos();
  const [marker, setMarker] = useState<google.maps.Marker>();
  const [lastGps, setLastGps] = useState<google.maps.LatLng | null>(null);
  const robotState = useRobotState()

  const gpsFix = useMsg() as IGPSMessage;

  useEffect(() => {
    let topic: Topic<any> | undefined;

    const callback = (msg: any) => {
      const dir = msg as { data: number };
      const offset_dir = dir.data;
      
      marker?.setIcon({
        path: (canHavePointer.includes(robotState) ? google.maps.SymbolPath.FORWARD_CLOSED_ARROW : google.maps.SymbolPath.CIRCLE),
        scale: 6,
        fillOpacity: 1,
        strokeWeight: 2,
        fillColor: "#5384ED",
        strokeColor: "#FFFFFF",
        rotation: offset_dir,
      });
    };

    if (!marker) {
      const newMarker = new google.maps.Marker();
      setMarker(newMarker);
    } else {
      topic = subscribe(
        buildTopicOptions(ROS, COMPASS_TOPIC.name, COMPASS_TOPIC.type, COMPASS_TOPIC.throttle),
        callback
      );
    }

    return () => {
      if (topic) {
        unsubscribe(topic);
      }
      if (marker) {
        marker.setMap(null);
        setMarker(undefined);
      }
    };
  }, [marker, robotState, ROS]);

  useEffect(() => {
    if (marker) {
      marker.setOptions(markerOptions);
    }
  }, [marker, markerOptions]);

  useDeepCompareEffect(() => {
    if (marker && gpsFix.latitude != null && gpsFix.longitude != null) {
      const gpsMsg = new google.maps.LatLng(gpsFix.latitude, gpsFix.longitude);
      setLastGps(gpsMsg);

      const map = marker.getMap();
      if (map && centerOnRobot) {
        map.setOptions({ center: gpsMsg });
      }

      marker.setZIndex(999);
      marker.setPosition(gpsMsg);
    }
  }, [gpsFix, marker, centerOnRobot]);

  const handleCenterClick = () => {
  if (marker && lastGps) {
    const map = marker.getMap();
    if (map) {
      map.setOptions({ center: lastGps });
    }
  }
};


  // Add the button as a Google Maps native control
  useEffect(() => {
    if (!marker) return;
    const map = marker.getMap();
    if (!map || centerOnRobot) return;

    const button = document.createElement("button");
    button.textContent = "Centre on Robot";
    button.style.background = "#5384ED";
    button.style.color = "white";
    button.style.border = "none";
    button.style.borderRadius = "4px";
    button.style.padding = "6px 12px";
    button.style.cursor = "pointer";
    button.style.margin = "10px";

    button.addEventListener("click", handleCenterClick);

    map.controls[google.maps.ControlPosition.TOP_RIGHT].push(button);

    return () => {
      button.removeEventListener("click", handleCenterClick);
      const controls = map.controls[google.maps.ControlPosition.TOP_RIGHT];
      for (let i = 0; i < controls.getLength(); i++) {
        if (controls.getAt(i) === button) {
          controls.removeAt(i);
          break;
        }
      }
    };
  }, [marker, lastGps, centerOnRobot]);

  return null;
};
